import { NadoDepositButtonState } from 'client/modules/collateral/nadoDeposit/types';
import { NadoWithdrawButtonState } from 'client/modules/collateral/nadoWithdraw/types';

export type NadoFundingAutomationFlow = 'deposit' | 'withdraw';

type ButtonStateByFlow<TFlow extends NadoFundingAutomationFlow> =
  TFlow extends 'deposit' ? NadoDepositButtonState : NadoWithdrawButtonState;

interface NadoFundingAutomationStatusParams<TFlow extends NadoFundingAutomationFlow> {
  flow: TFlow;
  buttonState: ButtonStateByFlow<TFlow>;
  featureEnabled: boolean;
  isPrivyEmbeddedWallet: boolean;
  isActive: boolean;
}

interface NadoFundingAutomationStopParams<TFlow extends NadoFundingAutomationFlow> {
  flow: TFlow;
  buttonState: ButtonStateByFlow<TFlow>;
  errorMessage: string | undefined;
}

interface NadoFundingAutomationSubmitParams<TFlow extends NadoFundingAutomationFlow>
  extends NadoFundingAutomationStatusParams<TFlow> {
  errorMessage: string | undefined;
  lastSubmittedButtonState: ButtonStateByFlow<TFlow> | undefined;
}

const DEPOSIT_AUTO_SUBMIT_STATES = new Set<NadoDepositButtonState>([
  'requires_switch_chain',
  'requires_approve',
  'ready_bridge',
  'ready_deposit',
]);

const WITHDRAW_AUTO_SUBMIT_STATES = new Set<NadoWithdrawButtonState>([
  'requires_switch_chain',
  'ready_withdraw',
  'requires_approve_oft',
  'ready_bridge',
]);

const DEPOSIT_STOP_STATES = new Set<NadoDepositButtonState>([
  'idle',
  'partial_arrival',
  'arrival_timeout',
  'route_unavailable',
  'wallet_mismatch',
  'success',
]);

const WITHDRAW_STOP_STATES = new Set<NadoWithdrawButtonState>([
  'idle',
  'partial_arrival',
  'arrival_timeout',
  'route_unavailable',
  'wallet_mismatch',
  'success',
]);

export function getNadoFundingAutomationStatus<TFlow extends NadoFundingAutomationFlow>({
  flow,
  buttonState,
  featureEnabled,
  isPrivyEmbeddedWallet,
  isActive,
}: NadoFundingAutomationStatusParams<TFlow>) {
  const isAvailable = featureEnabled && isPrivyEmbeddedWallet;
  const autoSubmitStates =
    flow === 'deposit' ? DEPOSIT_AUTO_SUBMIT_STATES : WITHDRAW_AUTO_SUBMIT_STATES;

  return {
    isAvailable,
    isActive: isAvailable && isActive,
    shouldAutoSubmit:
      isAvailable && isActive && autoSubmitStates.has(buttonState as never),
  };
}

export function shouldStopNadoFundingAutomation<TFlow extends NadoFundingAutomationFlow>({
  flow,
  buttonState,
  errorMessage,
}: NadoFundingAutomationStopParams<TFlow>) {
  if (errorMessage) {
    return true;
  }

  const stopStates = flow === 'deposit' ? DEPOSIT_STOP_STATES : WITHDRAW_STOP_STATES;
  return stopStates.has(buttonState as never);
}

export function shouldSubmitNadoFundingAutomationStep<TFlow extends NadoFundingAutomationFlow>({
  errorMessage,
  lastSubmittedButtonState,
  ...statusParams
}: NadoFundingAutomationSubmitParams<TFlow>) {
  if (
    shouldStopNadoFundingAutomation({
      flow: statusParams.flow,
      buttonState: statusParams.buttonState,
      errorMessage,
    })
  ) {
    return false;
  }

  const status = getNadoFundingAutomationStatus(statusParams);
  return (
    status.shouldAutoSubmit &&
    lastSubmittedButtonState !== statusParams.buttonState
  );
}

export function canStartNadoFundingAutomationStep<TFlow extends NadoFundingAutomationFlow>(
  statusParams: NadoFundingAutomationStatusParams<TFlow>,
) {
  const status = getNadoFundingAutomationStatus(statusParams);
  const autoSubmitStates =
    statusParams.flow === 'deposit' ? DEPOSIT_AUTO_SUBMIT_STATES : WITHDRAW_AUTO_SUBMIT_STATES;

  return status.isAvailable && autoSubmitStates.has(statusParams.buttonState as never);
}
